import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import logo from "../../../asset/images/bhagwatilogo.png";
import appConfig from "../../../config/appConfig";

const formatDate = (dateStr) => {
  if (!dateStr) {
    return "N/A";
  }

  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  const months = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];

  return `${day}-${months[date.getMonth()]}-${year}`;
};

const BhagwatiProductDetailsPage = () => {
  const { sku } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await axios.get(
          `${appConfig.baseUrl}/productfetch/${sku}`
        );

        if (response.data.success) {
          setProduct(response.data.data);
          return;
        }

        setError(response.data.error || "Product not found");
      } catch (err) {
        if (err.response?.status === 404) {
          setError(`Product not found with SKU: ${sku}`);
        } else {
          setError("Unable to fetch product details. Please try again later.");
        }

        console.error("Error fetching product:", err);
      } finally {
        setLoading(false);
      }
    };

    if (sku) {
      fetchProductDetails();
    }
  }, [sku]);

  if (loading) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-gray-100">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-300 border-t-[#295a80]" />
        <p className="mt-5 text-gray-600">Loading product details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-to-br from-slate-700 to-sky-900">
        <div className="max-w-lg rounded-lg bg-white p-8 text-center shadow-2xl">
          <h1 className="text-3xl font-semibold text-red-500">Product Not Found</h1>
          <p className="mt-4 text-gray-600">{error}</p>
          {sku && (
            <p className="mt-3 text-gray-700">
              SKU: <strong>{sku}</strong>
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      <div className="flex items-center justify-center gap-3 border-b-2 border-gray-200 bg-white p-4 shadow-sm md:gap-4 md:p-6">
        <img
          src={logo}
          alt="Company Logo"
          className="h-14 w-14 object-contain md:h-16 md:w-16"
        />
        <div className="text-center text-base font-semibold text-[#295a80] md:text-lg">
          SHREE BHAGWATI FLOUR & FOODS PVT. LTD.
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-6">
        <div className="overflow-hidden border-2 border-gray-400 bg-white shadow-md">
          <div className="border-b-2 border-gray-400 bg-[#295a80] px-4 py-3">
            <h2 className="text-center text-lg font-semibold text-white md:text-xl">
              Product Details
            </h2>
          </div>

          <div className="flex flex-col">
            {[
              ["Product Name", product.ProductName],
              ...(product.ProductCode ? [["Product Code", product.ProductCode]] : []),
              ["Lot No.", product.BatchName],
              ["Pkd.", formatDate(product.MfgDate)],
              ["Use By", formatDate(product.ExpDate)],
              ["Total MRP", `${product.MRP}/-`],
              ["RSN", sku],
            ].map(([label, value], index) => (
              <div
                key={label}
                className={`flex border-b border-gray-400 last:border-b-0 ${
                  index % 2 === 0 ? "bg-gray-50" : "bg-white"
                }`}
              >
                <div className="flex w-[45%] items-center border-r border-gray-400 px-3 py-3 text-sm font-bold text-[#295a80] sm:px-5 sm:text-[14px] lg:px-6 lg:text-[15px]">
                  {label}
                </div>
                <div className="flex w-[55%] items-center break-words px-4 py-3 text-sm font-medium text-gray-800 sm:px-5 sm:text-[15px] lg:px-6 lg:text-[16px]">
                  {value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BhagwatiProductDetailsPage;
